import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/hooks/useAuth";
import { Key } from "lucide-react";
import logo from "@/assets/logo.png";
import { useNavigate, Link } from "react-router-dom";
import { SEO } from "@/components/SEO";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import creator_img from "@/assets/login/creator.jpg";
import developer_img from "@/assets/login/studio.jpg";
import player_img from "@/assets/login/gamer.jpg";

export default function Auth() {
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [userType, setUserType] = useState<"developer" | "creator" | "player">("player");
  const [isCheckingDisplayName, setIsCheckingDisplayName] = useState(false);
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  const [isNewsletterSubscribed, setIsNewsletterSubscribed] = useState(false);
  const [honeypot, setHoneypot] = useState("");
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const { signIn, signUp } = useAuth();
  const navigate = useNavigate();

  // Handle Turnstile success callback
  useState(() => {
    (window as any).onTurnstileSuccessAuth = (token: string) => {
      setTurnstileToken(token);
    };
    return () => {
      delete (window as any).onTurnstileSuccessAuth;
    };
  });

  // --- AJOUT : Mapping des images ---
  const authImages = {
    developer: developer_img,
    creator: creator_img,
    player: player_img,
  };

  const checkDisplayNameUniqueness = async (name: string): Promise<boolean> => {
    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("id")
        .eq("display_name", name)
        .limit(1);

      if (error) {
        console.error("Error checking display name uniqueness:", error);
        return false;
      }

      return data.length === 0;
    } catch (error) {
      console.error("Error checking display name uniqueness:", error);
      return false;
    }
  };

  const checkEmailUniqueness = async (email: string): Promise<boolean> => {
    try {
      const { data, error } = await supabase.rpc('check_email_uniqueness', {
        email_to_check: email
      });

      if (error) {
        console.error("Error checking email uniqueness:", error);
        return false;
      }

      return data;
    } catch (error) {
      console.error("Error checking email uniqueness:", error);
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (honeypot) {
      return;
    }

    if (isSignUp) {
      if (!displayName.trim()) {
        toast.error("Display name is required");
        return;
      }

      setIsCheckingEmail(true);
      setIsCheckingDisplayName(true);

      try {
        const isEmailAvailable = await checkEmailUniqueness(email);

        if (!isEmailAvailable) {
          toast.error("This email is already registered. Please use a different email or try logging in.");
          setIsCheckingEmail(false);
          setIsCheckingDisplayName(false);
          return;
        }

        const isDisplayNameAvailable = await checkDisplayNameUniqueness(displayName.trim());

        if (!isDisplayNameAvailable) {
          toast.error("This display name is already taken. Please choose another one.");
          setIsCheckingEmail(false);
          setIsCheckingDisplayName(false);
          return;
        }

        await signUp(email, password, {
          user_type: userType,
          display_name: displayName.trim(),
          is_newsletter: isNewsletterSubscribed
        }, (window as any).turnstile?.getResponse() || turnstileToken || undefined);
      } catch (error) {
        console.error("Signup error:", error);
        toast.error("Failed to create account. Please try again.");
      } finally {
        setIsCheckingEmail(false);
        setIsCheckingDisplayName(false);
      }
    } else {
      await signIn(email, password);
    }
  };

  return (
    <div className="min-h-screen hero-gradient flex items-center justify-center p-4">
      <SEO
        title="Login / Sign Up"
        description="Log in to your IndieKeys account or sign up to start connecting with indie games and creators."
      />
      <div className="flex bg-background rounded-custom-paddings w-full" style={{ maxWidth: '1050px' }}>

        {/* --- MODIFICATION ICI : Image dynamique --- */}
        <div className="w-2/3 hidden sm:flex relative overflow-hidden transition-all duration-500">
          <img
            src={authImages[userType]}
            alt={`${userType} visual`}
            className="rounded-custom-paddings w-full h-full object-cover transition-opacity duration-300"
          />
        </div>

        <Card className="w-full p-8 card-gradient border-border/50 rounded-custom-paddings">
          <div className="flex justify-center mb-6 gap-3">
            <img src={logo} alt="IndieKeys" className="h-20" />
          </div>

          <p className="text-center text-muted-foreground mb-8">
            {isSignUp ? "Create your account" : "Welcome back"}
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div style={{ display: 'none' }}>
              <Label htmlFor="honeypot">Leave this field empty</Label>
              <Input
                id="honeypot"
                type="text"
                value={honeypot}
                onChange={(e) => setHoneypot(e.target.value)}
                tabIndex={-1}
                autoComplete="off"
              />
            </div>

            {isSignUp && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="displayName">Display Name</Label>
                  <Input
                    id="displayName"
                    type="text"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    required
                    className="bg-background/50"
                  />
                </div>

                <div className="space-y-3">
                  <Label>I am a:</Label>
                  <RadioGroup className="md:flex" value={userType} onValueChange={(value) => setUserType(value as "developer" | "creator" | "player")}>
                    <div className={`flex items-center space-x-2 p-3 rounded-custom-paddings border transition-colors cursor-pointer ${userType === 'developer' ? 'border-primary bg-primary/5' : 'border-border hover:border-primary'}`}>
                      <RadioGroupItem value="developer" id="developer" />
                      <Label htmlFor="developer" className="cursor-pointer flex-1">
                        Game Developer
                      </Label>
                    </div>
                    <div className={`flex items-center space-x-2 p-3 rounded-custom-paddings border transition-colors cursor-pointer ${userType === 'creator' ? 'border-primary bg-primary/5' : 'border-border hover:border-primary'}`}>
                      <RadioGroupItem value="creator" id="creator" />
                      <Label htmlFor="creator" className="cursor-pointer flex-1">
                        Content Creator
                      </Label>
                    </div>
                    <div className={`flex items-center space-x-2 p-3 rounded-custom-paddings border transition-colors cursor-pointer ${userType === 'player' ? 'border-primary bg-primary/5' : 'border-border hover:border-primary'}`}>
                      <RadioGroupItem value="player" id="player" />
                      <Label htmlFor="player" className="cursor-pointer flex-1">
                        Player
                      </Label>
                    </div>
                  </RadioGroup>
                </div>
              </>
            )}

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="bg-background/50"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                className="bg-background/50"
              />
            </div>

            {isSignUp && (
              <div className="flex items-start space-x-2">
                <Checkbox
                  id="newsletter"
                  checked={isNewsletterSubscribed}
                  onCheckedChange={(checked) => setIsNewsletterSubscribed(checked as boolean)}
                  className="mt-1"
                />
                <div className="grid gap-1.5 leading-none">
                  <Label
                    htmlFor="newsletter"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                  >
                    Subscribe to the newsletter
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    By subscribing, you agree to receive our newsletter game updates and events notifications. You can unsubscribe at any time. See our <Link to="/privacy-policy" className="underline hover:text-primary">Privacy Policy</Link>.
                  </p>
                </div>
              </div>
            )}

            {isSignUp && (
              <div className="flex justify-center my-4">
                <div
                  className="cf-turnstile"
                  data-sitekey="0x4AAAAAAACbx5Lh61AM6GeQ3"
                  data-callback="onTurnstileSuccessAuth"
                ></div>
              </div>
            )}

            <Button
              type="submit"
              className="w-full glow-effect"
              disabled={isCheckingDisplayName || isCheckingEmail}
            >
              {isCheckingDisplayName || isCheckingEmail ? "Checking availability..." : (isSignUp ? "Create Account" : "Sign In")}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={() => setIsSignUp(!isSignUp)}
              className="text-sm text-primary hover:underline"
            >
              {isSignUp ? "Already have an account? Sign in" : "Don't have an account? Sign up"}
            </button>
          </div>

          {!isSignUp && (
            <div className="mt-4 text-center">
              <Link
                to="/reset-password"
                className="text-sm text-muted-foreground hover:text-primary hover:underline"
              >
                Forgot your password?
              </Link>
            </div>
          )}

          <div className="mt-4 text-center">
            <button
              type="button"
              onClick={() => navigate("/")}
              className="text-sm text-muted-foreground hover:text-foreground"
            >
              Back to home
            </button>
          </div>
        </Card>
      </div>
    </div>
  );
}